import React from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { userRoutes, authRoutes, kioskRoutes, platformRoutes } from "./routes/allRoutes";
import ProtectedRoute from "./routes/ProtectedRoute";
import PlatformRoute from "./routes/PlatformRoute";
import PlatformProtectedRoute from "./routes/PlatformProtectedRoute";
import PlatformLayout from "./pages/Platform/components/PlatformLayout";
import AuthorizedRoute from "./routes/AuthorizedRoute";

import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { PlatformProvider } from "./contexts/PlatformContext";
import { MembersProvider } from "./contexts/MembersContext";
import { LayoutProvider } from "./contexts/LayoutContext";
import { AlertProvider } from "./contexts/AlertContext";

import { useLayout } from "./hooks/useLayout";

import "./assets/scss/theme.scss";
import "@phosphor-icons/web/light/style.css";
import 'react-toastify/dist/ReactToastify.css';
import "./services/firebase";
import { ToastContainer, Slide } from "react-toastify";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import PerformanceTracker from "./components/Common/PerformanceTracker";
import HelpWidget from "./components/Common/HelpWidget";

const PlataformRedirect = () => {
    const location = useLocation();
    const targetPath = location.pathname.replace(/^\/plataform/, "/platform");
    return <Navigate to={targetPath} replace />;
};

const AppContent = () => {
    const { layoutType } = useLayout();
    const Layout = layoutType === "horizontal" ? HorizontalLayout : VerticalLayout;

    return (
        <Routes>
            {/* Redirect /plataform (typo) to /platform */}
            <Route path="/plataform/*" element={<PlataformRedirect />} />
            <Route path="/plataform" element={<PlataformRedirect />} />

            {/* Non-authenticated routes */}
            {authRoutes.map((route, idx) => (
                <Route
                    key={idx}
                    path={route.path}
                    element={
                        <NonAuthLayout>{route.component}</NonAuthLayout>
                    }
                />
            ))}

            {/* Platform Admin routes (must come before tenant routes) */}
            {platformRoutes.map((route, idx) => (
                <Route
                    key={`platform-${idx}`}
                    path={route.path}
                    element={
                        <PlatformRoute>
                            <PlatformProtectedRoute platformPermission={route.platformPermission}>
                                <PlatformLayout>{route.component}</PlatformLayout>
                            </PlatformProtectedRoute>
                        </PlatformRoute>
                    }
                />
            ))}

            {/* Authenticated routes */}
            {userRoutes.map((route, idx) => (
                <Route
                    key={idx}
                    path={route.path}
                    element={
                        <ProtectedRoute>
                            <AuthorizedRoute
                                feature={route.feature}
                                permission={route.permission}
                                permissionsAny={route.permissionsAny}
                            >
                                <Layout>{route.component}</Layout>
                            </AuthorizedRoute>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* Kiosk routes (Authenticated, without layout) */}
            {kioskRoutes.map((route, idx) => (
                <Route
                    key={idx}
                    path={route.path}
                    element={
                        <ProtectedRoute>
                            <AuthorizedRoute
                                feature={route.feature}
                                permission={route.permission}
                                permissionsAny={route.permissionsAny}
                            >
                                {route.component}
                            </AuthorizedRoute>
                        </ProtectedRoute>
                    }
                />
            ))}
        </Routes>
    );
};

const App = () => {
    return (
        <ErrorBoundary>
            <AuthProvider>
                <PlatformProvider>
                <TenantProvider>
                    <MembersProvider>
                        <AlertProvider>
                            <LayoutProvider>
                                <PerformanceTracker />
                                <ToastContainer
                                    position="bottom-right"
                                    transition={Slide}
                                    autoClose={3000}
                                    hideProgressBar={false}
                                    newestOnTop={false}
                                    closeOnClick
                                    rtl={false}
                                    pauseOnFocusLoss
                                    draggable
                                    pauseOnHover
                                    theme="colored"
                                />
                                <AppContent />
                                <HelpWidget />
                            </LayoutProvider>
                        </AlertProvider>
                    </MembersProvider>
                </TenantProvider>
                </PlatformProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
};

export default App;
